import React, { useState } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import { ForecastMonthlyOverviewResponse, SaleMonthlyOverviewResponse } from '@ydistri/api-sdk';
import { styled } from 'styled-components';
import { DEFAULT_SALE_MONTH_COUNT, DetailType } from '../../../../../../../Detail/detailTypes';
import { NO_VALUE } from '../../../../../../../../lib/utils/utilsTypes';
import DetailSales from '../../../../../../../Detail/sections/DetailContentBodySection/components/DetailSales/DetailSales';
import SkuDailyTransactionsChart from '../../../../../../../Detail/sections/SkuDailyTransactionsChart/SkuDailyTransactionsChart';
import { Column, ReadOnlyItem, Row, Section, Title } from '@ydistri/ds';
import { SKU_ID_LABEL } from '../../../../../../../../components/global/_constants';
import { useTemplateOrCalculation } from '../../../../../../../../hooks/useTemplateOrCalculation';
import {
  useGetMonthlyForecastQuery,
  useGetMonthlySalesQuery,
} from '../../../../../../../Calculations/apiCalculationsSkus';
import ButtonOptions from '../../../../../../../../components/global/ButtonOptions/ButtonOptions';
import PopoverCloseButton from '../../../../../../../../components/global/PopoverCloseButton/PopoverCloseButton';
import { Units } from '../../../../../../../../lib/charts/saleChartsTypes';
import { useGetSkuQuery } from '../../../../../../../Detail/apiDetail';
import ProductIdentifications from '../../../../../../../../components/global/ProductIdentifications/ProductIdentifications';
import StoreIdentifications from '../../../../../../../../components/global/StoreIdentifications/StoreIdentifications';
import { SystemIdMode } from '../../../../../../../../components/global/Identifications/useIdentificationItems';

const HeaderIdentifications = styled(Row)`
  overflow-wrap: anywhere;
`;

const EMPTY_SALES_DATA: SaleMonthlyOverviewResponse[] = [];
const EMPTY_FORECAST_DATA: ForecastMonthlyOverviewResponse[] = [];
const DAILY_CHART_MIN_HEIGHT = '30rem';
const CHART_WRAPPER_HEIGHT = '45rem';

const ChartWrapper = styled.div`
  height: ${CHART_WRAPPER_HEIGHT};
  max-height: calc(100vh - 12rem);
  overflow-x: hidden;
  overflow-y: auto;
`;

type SkuSaleChartMode = 'monthly' | 'daily';

const CHART_MODE_OPTIONS: { label: string; value: SkuSaleChartMode }[] = [
  {
    label: 'Monthly',
    value: 'monthly',
  },
  {
    label: 'Daily',
    value: 'daily',
  },
];

interface SkuSalePopoverChartProps {
  skuId: number;
  onClose: () => void;
}

const SkuSalePopoverChart: React.FC<SkuSalePopoverChartProps> = ({ skuId, onClose }) => {
  const [chartMode, setChartMode] = useState<SkuSaleChartMode>('monthly');
  const calculationData = useTemplateOrCalculation();
  const calculationId = calculationData.id;
  const monthlyChartDisabled = skuId === NO_VALUE || chartMode !== 'monthly';
  const { currentData: sku, isFetching: skuFetching } = useGetSkuQuery(
    skuId === NO_VALUE ? skipToken : skuId,
  );

  // `currentData` prevents briefly showing a previous SKU while the new query is in flight.
  const { currentData: salesData = EMPTY_SALES_DATA, isFetching: salesDataFetching } =
    useGetMonthlySalesQuery(
      monthlyChartDisabled
        ? skipToken
        : {
            calculationId,
            skuId,
            monthsCount: DEFAULT_SALE_MONTH_COUNT,
          },
    );
  const { currentData: forecastData = EMPTY_FORECAST_DATA, isFetching: forecastDataFetching } =
    useGetMonthlyForecastQuery(
      monthlyChartDisabled
        ? skipToken
        : {
            calculationId,
            skuId,
          },
    );

  const loadingSalesData = salesDataFetching || forecastDataFetching;

  if (skuId === NO_VALUE) {
    return null;
  }

  return (
    <Column $gap="1rem" $width="min(80rem, calc(100vw - 4rem))">
      <Row
        as="header"
        aria-label="Sales details"
        $alignItems="flex-start"
        $gap="1rem"
        $flexWrap="nowrap"
      >
        <HeaderIdentifications $flex="1" $minWidth="0" $gap="2rem">
          <Row $gap="1rem" $minWidth="0">
            <Title>
              Product: {sku?.product?.name ?? (skuFetching ? 'Loading...' : 'Unavailable')}
            </Title>
            {sku?.product && (
              <ProductIdentifications
                product={sku.product}
                systemIdMode={SystemIdMode.DIFFERENT_ONLY}
              />
            )}
          </Row>
          <Row $gap="1rem" $minWidth="0">
            <Title>Store: {sku?.store?.name ?? (skuFetching ? 'Loading...' : 'Unavailable')}</Title>
            {sku?.store && (
              <StoreIdentifications store={sku.store} systemIdMode={SystemIdMode.DIFFERENT_ONLY} />
            )}
          </Row>
          <ReadOnlyItem label={SKU_ID_LABEL} value={skuId.toString()} />
        </HeaderIdentifications>
        <PopoverCloseButton onClose={onClose} />
      </Row>
      <ButtonOptions value={chartMode} setValue={setChartMode} options={CHART_MODE_OPTIONS} />
      <ChartWrapper>
        {chartMode === 'daily' ? (
          <Section header={`Daily sales in ${Units.QUANTITY} [MU]`} $basis={30}>
            <SkuDailyTransactionsChart skuId={skuId} chartMinHeight={DAILY_CHART_MIN_HEIGHT} />
          </Section>
        ) : (
          <DetailSales
            salesData={salesData}
            forecastData={forecastData}
            loadingSalesData={loadingSalesData}
            hasForecasts={true}
            skuChart={true}
            showAverageValueInChartTooltip={true}
            forcedDetailType={DetailType.SKU}
          />
        )}
      </ChartWrapper>
    </Column>
  );
};

export default SkuSalePopoverChart;
